import React, { FC, useCallback, useMemo } from "react"
import { OrgDashboardBase } from "../DashboardBase/OrgDashboardBase"
import { Table } from "resha"
import { TableHeader } from "../../../../Common/Table/TableHeader"
import { SortChooserProps } from "../../../../Common/Button/SortChooser"
import { getValue, hasValue } from "../../../../../Models/Client/FetchState"
import { Loader, Button } from "dash-styled"
import { useOrgMemberList } from "../../../../../Hooks/useOrgMemberList"
import { memberColumns, orgMemberSortOptions } from "./Common/constants"
import {
    APIOrgMember,
    APIOrgMemberRole,
    getOrgId
} from "../../../../../Models/API/APIOrgMember"
import { OrgMemberAddModal } from "./Common/OrgMemberAddModal"
import {
    inviteToOrg,
    changeOrgMemberRole,
    removeMemberFromOrg
} from "../../../../../Actions/API/Org/OrgMemberActions"
import { OrgMemberChangeRoleModal } from "./Common/OrgMemberChangeRoleModal"
import { Confirm } from "../../../../Common/Modal/Confirm"
import { useMyOrgRole } from "../../../../../Hooks/useMyOrgRole"
import { routePaths } from "../../../../../Routes"
import { useModal } from "../../../../../Hooks/useModal"
import { SortExpression } from "../../../../../Models/Client/SortExpression"
import { useDispatch } from "react-redux"
import { push } from "connected-react-router"
import { getEmail } from "../../../../../Helpers/Functions/Selectors/auth"
import { useAppState } from "../../../../../Hooks/useReduxState"

type Modal =
    | { type: "add" }
    | { type: "remove"; id: number; name: string }
    | { type: "changeRole"; id: number; currentRole: APIOrgMemberRole }

const rowActions = ["Change role", "Remove from Organization"] as const

export const MemberList: FC<{
    orgUsername: string
    sort: SortExpression<APIOrgMember>
}> = ({ orgUsername, sort }) => {
    const [membersFetch, handleLoadMore] = useOrgMemberList(orgUsername, sort)
    const myRoleFetch = useMyOrgRole(orgUsername)
    const { modal, isSaving, save, close, open } = useModal<Modal>()
    const myEmail = useAppState(getEmail)
    const dispatch = useDispatch()
    const sortOptions = useMemo<SortChooserProps<APIOrgMember>>(
        () => ({
            fields: orgMemberSortOptions,
            applied: sort,
            onChange: newSort =>
                dispatch(push(routePaths.orgMemberList(orgUsername, newSort)))
        }),
        [dispatch, orgUsername, sort]
    )
    const handleRowAction = useCallback(
        (selected: typeof rowActions[number], id: number) => {
            const member = getValue(membersFetch)?.items.find(
                member => member.id === id
            )

            if (member === undefined) {
                return
            }

            switch (selected) {
                case "Change role": {
                    open({
                        type: "changeRole",
                        id,
                        currentRole: member.role
                    })
                    return
                }
                case "Remove from Organization":
                    open({
                        type: "remove",
                        name: member.userEmail,
                        id
                    })
                    return
            }
        },
        [membersFetch, open]
    )
    const columns = useMemo(
        () =>
            memberColumns(
                model =>
                    model.userEmail !== myEmail
                        ? ["Change role", "Remove from Organization"]
                        : [],
                myEmail ?? "",
                handleRowAction
            ),
        [handleRowAction, myEmail]
    )
    const handleAdd = useCallback(() => open({ type: "add" }), [open])
    const handleChangeRole = useCallback(
        (role: APIOrgMemberRole) => {
            if (modal?.type === "changeRole") {
                save(changeOrgMemberRole(orgUsername, modal.id, role))
                return
            }
        },
        [modal, orgUsername, save]
    )
    const handleAddMember = useCallback(
        (emails: string[], role: APIOrgMemberRole) => {
            if (modal?.type === "add") {
                save(inviteToOrg(orgUsername, emails, role))
            }
        },
        [modal, orgUsername, save]
    )
    const handleRemoveMember = useCallback(() => {
        if (modal?.type === "remove") {
            save(removeMemberFromOrg(orgUsername, modal.id))
        }
    }, [modal, orgUsername, save])

    const count = getValue(membersFetch)?.count
    const props = {
        title: "Members",
        description: "Collaborators in your organizations",
        orgUsername,
        fixedContentWidth: false
    }

    if (!hasValue(myRoleFetch)) {
        return (
            <OrgDashboardBase {...props}>
                <Loader />
            </OrgDashboardBase>
        )
    }

    return (
        <OrgDashboardBase {...props}>
            <Button className="w-48" onClick={handleAdd}>
                Add member
            </Button>
            <TableHeader
                filteredCount={count}
                totalCount={getValue(membersFetch)?.totalCount}
                sortOptions={sortOptions}
            />
            <div className="flex-grow h-0 flex-basis-0 overflow-y-auto -m-6 mt-0 px-6">
                <Table
                    className="w-full"
                    columns={columns}
                    getKey={getOrgId}
                    items={getValue(membersFetch)?.items}
                    itemCount={count}
                    stickyHeader={true}
                    loadMore={handleLoadMore}
                />
            </div>
            {modal?.type === "add" && (
                <OrgMemberAddModal
                    isSaving={isSaving}
                    onClose={close}
                    onSubmit={handleAddMember}
                />
            )}
            {modal?.type === "changeRole" && (
                <OrgMemberChangeRoleModal
                    currentRole={modal.currentRole}
                    isSaving={isSaving}
                    onClose={close}
                    onSubmit={handleChangeRole}
                />
            )}
            {modal?.type === "remove" && (
                <Confirm
                    title={`Remove ${modal.name} from ${orgUsername}?`}
                    yesLabel={isSaving ? "Removing..." : "Remove"}
                    noLabel="Cancel"
                    isProcessing={isSaving}
                    onClose={close}
                    onClickNo={close}
                    onClickYes={handleRemoveMember}
                />
            )}
        </OrgDashboardBase>
    )
}
